<template>
  <div
    class="card h-100"
    :class="{
      'is-selected': isSelected,
      'is-multi-select-mode': isMultiSelectMode,
      'is-selectable': isMultiSelectMode,
      'is-video-clickable': (isVideoAsset || previewAll) && !isMultiSelectMode
    }"
    @click="handleCardClick"
  >
    <div class="card-image">
      <!-- Multi-select checkbox overlay -->
      <div v-if="isMultiSelectMode" class="multi-select-overlay">
        <div class="multi-select-checkbox" :class="{ 'has-number': selectionOrder !== null && isSelected }">
          <!-- Show number when selectionOrder is provided and selected -->
          <span v-if="selectionOrder !== null && isSelected" class="selection-number">
            {{ selectionOrder }}
          </span>
          <!-- Show icon when no selectionOrder or not selected -->
          <span v-else class="icon">
            <font-awesome-icon
              :icon="['fal', 'circle']"
              active-icon="fa-duotone fa-light fa-circle-check"
              :active="isSelected"
              :class="{
                'has-text-primary': isSelected,
                'has-text-white': !isSelected,
              }"
            />
          </span>
        </div>
      </div>

      <figure
        class="image is-16by9"
        @mouseenter="onCardHoverEnter"
        @mouseleave="onCardHoverLeave"
      >
        <div class="media-container">
          <!-- Skeleton overlay shown while we're fetching the URL or
               waiting for the image to decode. Sits above .media-element
               via z-index; cleared once the first @load fires. -->
          <div
            v-if="!imageHasLoaded && hasThumbnailSource(item)"
            class="thumbnail-skeleton"
          ></div>
          <img
            v-if="displayedImageSrc"
            :src="displayedImageSrc"
            :alt="item.name"
            @load="onImageLoad"
            @error="handleImageError"
            class="media-element"
          />
          <div v-else class="default-thumbnail" :class="{ 'is-audio': isAudioAsset }">
            <font-awesome-icon
              :icon="['fal', getDefaultThumbnailIcon]"
              class="default-thumbnail-icon"
            />
          </div>

          <!-- Duration - Bottom Left -->
          <div v-if="displayDuration" class="duration-overlay">
            <div class="duration-badge">
              <font-awesome-icon
                :icon="['fal', 'clock']"
                class="mr-1"
                style="font-size: 10px"
              />
              <span>{{ formatDuration(displayDuration) }}</span>
            </div>
          </div>

          <!-- File extension - Bottom Right -->
          <div v-if="fileExtension" class="file-ext-overlay">
            <div class="file-ext-overlay-badge">
              {{ fileExtension }}
            </div>
          </div>

          <!-- Preview button for videos - Top Left (in multi-select mode) or clickable card (normal mode) -->
          <div 
            v-if="isVideoAsset && isMultiSelectMode" 
            class="preview-button-overlay"
            @click.stop="handlePreview"
          >
            <button class="preview-button" aria-label="Preview video">
              <span class="icon">
                <font-awesome-icon :icon="['fas', 'play']" />
              </span>
            </button>
          </div>
        </div>
      </figure>
    </div>

    <!-- Bottom info bar -->
    <div class="card-content p-3">
      <div class="is-flex is-align-items-center">
        <span v-if="item.assetType" class="icon is-small mr-2 has-text-primary">
          <font-awesome-icon :icon="['fal', getAssetIconName(item.assetType)]" />
        </span>

        <div class="media-info is-flex-grow-1">
          <p class="title is-size-7 mb-0 has-text-weight-medium asset-name">
            {{ item.name.replace(/_/g, " ") }}
          </p>
        </div>
      </div>

      <!-- Transcript search hits: a snippet of what was said plus one clickable
           timestamp chip per mention. Each chip jumps the player to that moment;
           .stop keeps a chip click from also triggering the card's open action. -->
      <div v-if="transcriptMatch" class="transcript-hit mt-2">
        <p class="transcript-hit-snippet is-size-7 has-text-grey">
          <span class="icon is-small transcript-hit-icon">
            <font-awesome-icon :icon="['fal', 'quote-left']" />
          </span>
          <span class="transcript-hit-text">{{ transcriptMatch.snippet }}</span>
        </p>
        <div class="transcript-hit-chips">
          <button
            v-for="hit in transcriptMatch.hits"
            :key="hit.start"
            type="button"
            class="transcript-chip"
            :title="`Jump to ${hit.label}`"
            @click.stop="$emit('seek', hit.start)"
          >
            {{ hit.label }}
          </button>
          <span v-if="transcriptMatch.moreCount" class="transcript-more is-size-7 has-text-grey">
            +{{ transcriptMatch.moreCount }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import {
  getAssetIconName,
  getFileTypeIconName,
} from "@/utils/assetUtils";
import { useClientAssetsStore } from "@/stores/clientAssetsStore";
import { logger } from "@/services/loggerService";

const props = defineProps({
  item: {
    type: Object,
    required: true,
    validator: (obj) => {
      return obj.name && obj.assetType;
    },
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  isMultiSelectMode: {
    type: Boolean,
    default: false,
  },
  selectionOrder: {
    type: Number,
    default: null,
  },
  previewAll: {
    type: Boolean,
    default: false,
  },
  // Set when the current library search matched this asset's transcript:
  // { start, snippet, term }. Shows an "in transcript" snippet on the card.
  transcriptMatch: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['preview', 'seek']);

const thumbnailUrl = ref(null);
// One-way flag: flips true on the first successful <img> @load and never
// resets, so the skeleton doesn't flash over an already-painted thumbnail
// when the src swaps to preview.webp on hover.
const imageHasLoaded = ref(false);
const clientAssetsStore = useClientAssetsStore();

// Hover state — used to swap the static thumbnail for Bunny's animated
// preview WebP when present. The preview URL is pre-signed at transcode
// time and cached on the asset doc (`bunnyStream.previewUrl`), so the
// swap is instant — no Cloud Function call per hover.
const isHovered = ref(false);
let previewWarmupTimer = null;

const cachedPreviewUrl = computed(() => {
  const expires = props.item?.bunnyStream?.previewUrlExpires || 0;
  const nowSec = Math.floor(Date.now() / 1000);
  if (expires > nowSec) {
    return props.item?.bunnyStream?.previewUrl || null;
  }
  return null;
});

// Static thumbnail is pre-signed and cached on the asset doc by
// cacheBunnyPreviewUrls (videos → bunnyStream.thumbnailUrl) and
// cacheBunnyImageUrls (images → bunnyStorage.thumbnailUrl), both with a
// 30-day TTL. Reading synchronously here avoids the Cloud Function
// round-trip that the async loader otherwise triggers on every card
// mount. The async loader (thumbnailUrl) stays as the fallback for
// assets without a cached URL (legacy docs, expired TTL).
const cachedThumbnailUrl = computed(() => {
  const nowSec = Math.floor(Date.now() / 1000);
  const streamExpires = props.item?.bunnyStream?.thumbnailUrlExpires || 0;
  if (streamExpires > nowSec && props.item?.bunnyStream?.thumbnailUrl) {
    return props.item.bunnyStream.thumbnailUrl;
  }
  const storageExpires = props.item?.bunnyStorage?.thumbnailUrlExpires || 0;
  if (storageExpires > nowSec && props.item?.bunnyStorage?.thumbnailUrl) {
    return props.item.bunnyStorage.thumbnailUrl;
  }
  return null;
});

// Image src that's actually rendered. Animated preview while hovered (if
// cached); otherwise prefer the synchronously-cached thumbnail, falling
// back to the async-loaded thumbnail URL when no cache is available.
const displayedImageSrc = computed(() => {
  if (isHovered.value && cachedPreviewUrl.value) return cachedPreviewUrl.value;
  return cachedThumbnailUrl.value || thumbnailUrl.value;
});

// Briefly delay setting hover so cards aren't pummeled with WebP loads
// during fast cursor traversal across a grid. 150ms is below the ~200ms
// threshold where a delay starts feeling laggy but long enough that
// drag-by hovers don't trigger.
const onCardHoverEnter = () => {
  if (previewWarmupTimer) clearTimeout(previewWarmupTimer);
  previewWarmupTimer = setTimeout(() => {
    isHovered.value = true;
  }, 150);
};

const onCardHoverLeave = () => {
  if (previewWarmupTimer) {
    clearTimeout(previewWarmupTimer);
    previewWarmupTimer = null;
  }
  isHovered.value = false;
};

const displayDuration = computed(() => {
  return props.item.duration || null;
});

const getDefaultThumbnailIcon = computed(() => {
  return getFileTypeIconName({
    mimeType: props.item.type || props.item.mimeType,
    fileName: props.item.name,
    assetType: props.item.assetType,
  });
});

const isVideoAsset = computed(() => {
  return props.item.assetType === "videos" || 
         props.item.type?.startsWith("video/");
});

const isAudioAsset = computed(() => {
  return props.item.assetType === "audio" ||
         props.item.type?.startsWith("audio/");
});


const MIME_TO_EXT = {
  'application/pdf': 'PDF', 'application/zip': 'ZIP',
  'application/msword': 'DOC', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.ms-excel': 'XLS', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.ms-powerpoint': 'PPT', 'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  'image/jpeg': 'JPG', 'image/png': 'PNG', 'image/gif': 'GIF', 'image/webp': 'WEBP', 'image/svg+xml': 'SVG',
  'image/heic': 'HEIC', 'image/tiff': 'TIFF', 'application/postscript': 'EPS', 'application/eps': 'EPS', 'image/x-eps': 'EPS',
  'video/mp4': 'MP4', 'video/quicktime': 'MOV', 'video/webm': 'WEBM',
  'audio/mpeg': 'MP3', 'audio/wav': 'WAV', 'audio/aac': 'AAC', 'audio/flac': 'FLAC', 'audio/ogg': 'OGG',
  'text/plain': 'TXT', 'text/csv': 'CSV',
};

// ponytail: an extension is short and has a letter. Guards against dotted
// names like "Still_2025-02-14_145208_1.60.2" (extension already stripped by
// canonicalAssetName), which otherwise badge as "2".
const looksLikeExtension = (s) => /^[a-z0-9]{1,5}$/i.test(s) && /[a-z]/i.test(s);

const fileExtension = computed(() => {
  // Try original filename first (has extension)
  const originalName = props.item.metadata?.originalName || props.item.originalName;
  const originalExt = originalName?.includes('.') ? originalName.split('.').pop() : null;
  if (originalExt && looksLikeExtension(originalExt)) {
    return originalExt.toUpperCase();
  }
  // Fall back to display name if it has an extension
  const nameExt = props.item.name?.includes('.') ? props.item.name.split('.').pop() : null;
  if (nameExt && looksLikeExtension(nameExt)) {
    return nameExt.toUpperCase();
  }
  // Fall back to MIME type
  const mime = props.item.type || props.item.contentType || props.item.mimeType;
  if (mime) return MIME_TO_EXT[mime.toLowerCase()] || null;
  return null;
});

const handleImageError = () => {
  thumbnailUrl.value = null;
};

const onImageLoad = () => {
  imageHasLoaded.value = true;
};

const handlePreview = () => {
  logger.debug("[MediaCard] Preview button clicked", {
    assetId: props.item.id,
    name: props.item.name
  });
  emit('preview', props.item);
};

const handleCardClick = () => {
  if (props.previewAll && !props.isMultiSelectMode) {
    handlePreview();
  }
};

const formatDuration = (duration) => {
  try {
    if (!duration || typeof duration !== "number") return "";
    // Convert seconds to MM:SS format
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  } catch (error) {
    logger.error("[MediaCard] Error formatting duration:", {
      duration,
      error: error.message,
    });
    return "";
  }
};

// Whether this asset has a *renderable raster* thumbnail to put in an <img>.
// Distinct from the shared `hasBunnyThumbnail`, which counts ANY
// `bunnyStorage.path` — but a document (PDF/EPS) or audio storage object is
// the file itself, not a displayable image. Treating those as a thumbnail
// source made the skeleton shimmer flash, then the <img> error out, then fall
// back to the icon. Here, a Bunny storage object only counts when the asset is
// an image; documents need a separately-rasterized preview
// (`bunnyThumbnail.path`, e.g. the page-1 JPEG from generateBunnyPdfThumbnail)
// and videos use the Stream poster.
const hasThumbnailSource = (item) => {
  if (!item) return false;
  const isImage = item.assetType === 'images' || item.type?.startsWith('image/');
  return (
    !!item.thumbnail ||                       // legacy Firebase JPEG (incl. old PDF page-1)
    !!item.bunnyStream?.thumbnailPath ||      // Bunny Stream video poster
    !!item.bunnyThumbnail?.path ||            // Bunny rasterized PDF page-1 JPEG
    (!!item.bunnyStorage?.path && isImage)    // image storage object is its own thumbnail
  );
};

// Function to load thumbnail URL using the store's method
const loadThumbnailUrl = async () => {
  if (!hasThumbnailSource(props.item)) {
    thumbnailUrl.value = null;
    return;
  }
  // Skip the async loader if the cached URL is already serving the
  // template. Otherwise the trigger writes that populate the cache
  // (cacheBunnyImageUrls / cacheBunnyPreviewUrls) would each fire a
  // redundant getBunnyAssetUrl Cloud Function call.
  if (cachedThumbnailUrl.value) {
    thumbnailUrl.value = null;
    return;
  }
  try {
    thumbnailUrl.value = await clientAssetsStore.getThumbnailUrl(props.item);
  } catch (error) {
    logger.error("[MediaCard] Failed to load thumbnail:", {
      error: error.message,
      name: props.item.name,
      id: props.item.id,
    });
    thumbnailUrl.value = null;
  }
};

// Load thumbnail on mount
onMounted(() => {
  loadThumbnailUrl();
});

watch(
  () => [
    props.item.thumbnail,
    props.item.thumbnailCacheBuster,
    props.item.thumbnailUpdatedAt,
    props.item.bunnyStream?.thumbnailPath,
    props.item.bunnyStream?.thumbnailUrl,
    props.item.bunnyStorage?.path,
    props.item.bunnyStorage?.thumbnailUrl,
  ],
  () => {
    loadThumbnailUrl();
  }
);
</script>

<style lang="scss" scoped>
.card {
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }

  &.is-selectable:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &.is-selected {
    transform: scale(0.95);
    border: 2px dashed var(--bulma-primary);
    padding: 0.5rem;
    background-color: transparent;
    box-shadow: none;

    .card-content,
    .card-image {
      border-radius: 6px;
      overflow: hidden;
    }
  }

  &.is-multi-select-mode {
    .card-image {
      position: relative;
    }
  }
}

.multi-select-overlay {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 10;

  .multi-select-checkbox {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    transition: all 0.2s ease;

    // Slightly larger indicator on touch devices for visibility
    @media (hover: none) and (pointer: coarse) {
      width: 2.5rem;
      height: 2.5rem;

      .icon {
        font-size: 1.5rem;
      }
    }

    .icon {
      font-size: 1.25rem;
      filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
    }

    .selection-number {
      font-size: 0.875rem;
      font-weight: 600;
      color: white;
      line-height: 1;
    }

    &.has-number {
      background-color: var(--bulma-primary);
    }

    &:hover {
      background-color: rgba(0, 0, 0, 0.7);
      transform: scale(1.1);
    }

    &.has-number:hover {
      background-color: var(--bulma-primary);
      filter: brightness(1.1);
    }
  }
}

// Selected state for the checkbox
.is-selected .multi-select-overlay .multi-select-checkbox {
  background-color: rgba(
    var(--bulma-primary-h),
    var(--bulma-primary-s),
    var(--bulma-primary-l),
    0.9
  );

  // Solid background when showing numbers
  &.has-number {
    background-color: var(--bulma-primary);
  }

  &:hover {
    background-color: var(--bulma-primary);
  }
}

.card-image {
  background: #000;
  position: relative;

  .image {
    position: relative;
    overflow: hidden;
    border-radius: 0;
  }
}

.media-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url("@/assets/img/assetbg.png") center center repeat;
  background-size: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

// Fill the frame box and let `contain` scale the image (up or down) until one
// side touches the edges — aspect ratio kept, nothing cropped.
.media-element {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

// Shimmer skeleton overlaid while the thumbnail loads. Mirrors the
// kanban-board skeleton pattern in DeliverableKanbanBoard.vue for visual
// consistency across loading states.
.thumbnail-skeleton {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04) 25%,
    rgba(255, 255, 255, 0.12) 50%,
    rgba(255, 255, 255, 0.04) 75%
  );
  background-size: 200% 100%;
  animation: media-card-skeleton-loading 1.5s infinite;
}

@keyframes media-card-skeleton-loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}


.default-thumbnail {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  // No fill — let the .media-container transparency/checkerboard grid show
  // through behind document/icon assets (PDF, EPS, etc.).
  background: transparent;

  // Special background for audio files using Bulma primary color
  &.is-audio {
    // Using CSS variables for Bulma primary color HSL values
    // The second color is a darker version with reduced lightness
    background: linear-gradient(
      135deg, 
      hsl(var(--bulma-primary-h), var(--bulma-primary-s), var(--bulma-primary-l)),
      hsl(var(--bulma-primary-h), var(--bulma-primary-s), calc(var(--bulma-primary-l) - 15%))
    );
  }
}

.default-thumbnail-icon {
  font-size: 3rem;
  // Brand-color the icon (reflects company primary during impersonation),
  // sitting on the transparency grid behind it.
  color: hsl(var(--bulma-primary-h), var(--bulma-primary-s), var(--bulma-primary-l));

  // Special styling for audio waveform icon (sits on the primary gradient fill)
  .is-audio & {
    font-size: 3.5rem;
    color: rgba(255, 255, 255, 0.95);
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  }
}

.duration-overlay {
  position: absolute;
  bottom: 0.5rem;
  left: 0.5rem;
  z-index: 5;
}

.file-ext-overlay {
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  z-index: 5;
}

.duration-badge,
.file-ext-overlay-badge {
  display: flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  border-radius: 4px;
  color: white;
  font-size: 0.65rem;
  font-weight: 600;
  white-space: nowrap;
  letter-spacing: 0.025em;
  text-transform: uppercase;
}

figure {
  border-radius: 0;
  margin: 0;
  background: #000;
}

.media-info {
  min-width: 0;
  flex: 1;

  .asset-name {
    color: #333;
    line-height: 1.3;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
  }
}

.card-content {
  background: white;
  border-top: 1px solid #f0f0f0;
}

// Transcript search hits — subtle snippet (two-line clamp so long cues don't
// stretch the card) above a wrap of clickable timestamp chips.
.transcript-hit {
  .transcript-hit-snippet {
    display: flex;
    align-items: flex-start;
    gap: 0.35rem;
    line-height: 1.25;
    margin-bottom: 0.4rem;
  }

  .transcript-hit-icon {
    flex: none;
    color: var(--primary, #888);
    opacity: 0.7;
    margin-top: 0.1rem;
  }

  .transcript-hit-text {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-style: italic;
  }

  .transcript-hit-chips {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.3rem;
  }

  .transcript-chip {
    font-size: 0.7rem;
    line-height: 1;
    padding: 0.2rem 0.45rem;
    border: 1px solid #e0e0e0;
    border-radius: 999px;
    background: #fafafa;
    color: var(--primary, #555);
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover {
      background: var(--primary, #eee);
      border-color: var(--primary, #ccc);
      color: #fff;
    }
  }
}

.preview-button-overlay {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  z-index: 5;
  opacity: 0;
  transition: opacity 0.2s ease;

  .preview-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    color: white;

    .icon {
      font-size: 1.25rem;
      filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
    }

    &:hover {
      background-color: rgba(0, 0, 0, 0.7);
      transform: scale(1.1);
    }

    &:active {
      transform: scale(0.95);
    }
  }
}

.card:hover .preview-button-overlay,
.preview-button-overlay:hover {
  opacity: 1;
}

@media (hover: none) {
  .preview-button-overlay {
    opacity: 1;
  }
}

.is-video-clickable {
  cursor: pointer;
  
  &:hover {
    .card-content {
      background-color: rgba(var(--bulma-primary-h), var(--bulma-primary-s), var(--bulma-primary-l), 0.05);
    }
  }
}
</style>
